import type { ReactNode } from 'react';

import { Box, Link, Typography, ListItemText } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import type { Theme, SxProps } from '@mui/material/styles';

import { Iconify } from 'src/components/iconify';
import { Label } from 'src/components/label';
import ClientAvatar from 'src/components/client-avatar/ClientAvatar';
import UserAvatar from 'src/components/user-avatar/UserAvatar';
import CountryWithFlag from 'src/components/country-with-flag/CountryWithFlag';
import ShortenCode from 'src/components/shorten-code/ShortenCode';
import type { useTranslate } from 'src/locales';
import { fCurrency } from 'src/utils/format-number';
import { fDate, fIsBeforeToday } from 'src/utils/format-time';
import { getTransactionTypeLabel } from 'src/utils/helpers';
import {
  PaymentTransactionType,
  DEBT_COLLECTION_INBOUND_PAYMENT_TRANSACTION_TYPES,
} from 'src/types/payments';
import {
  InvoiceStage,
  InvoiceStatus,
  type IBaseInvoice,
  InvoicePaymentStatus,
} from 'src/types/invoices';

import { InvoiceColumns } from 'src/bop-view-definitions';
import { INVOICE_ACTION_COLUMN_ID } from 'src/hooks/useCustomizableTable/lib/tableColumnDefinitions';

type Translate = ReturnType<typeof useTranslate>['t'];

/**
 * Payment types worth calling out underneath a paid invoice's status — the
 * invoice is settled, but not by an ordinary payment.
 */
const LAST_PAYMENT_TYPES_TO_SHOW = [
  PaymentTransactionType.CreditLoss,
  PaymentTransactionType.GoodWill,
  PaymentTransactionType.Cancelled,
  PaymentTransactionType.PaymentToDC,
  PaymentTransactionType.CreditAmountToDC,
  PaymentTransactionType.GoodwillWaiveToDC,
  ...DEBT_COLLECTION_INBOUND_PAYMENT_TRANSACTION_TYPES,
];

const AMOUNT_COLUMN_IDS: readonly InvoiceColumns[] = [
  InvoiceColumns.TOTAL_REMAINING_BALANCE,
  InvoiceColumns.ORIGINAL_TOTAL_AMOUNT,
  InvoiceColumns.PAID_AMOUNT,
  InvoiceColumns.VAT_AMOUNT,
];

/**
 * The invoice row as the cells read it. Reconciles two mismatches with
 * `IBaseInvoice`: `serviceLineId` is delivered by the invoice views but not
 * declared there, and `daysOverdue` is declared as a `Date` while the API
 * delivers a scalar.
 */
type InvoiceCellRow = Omit<IBaseInvoice, 'daysOverdue'> & {
  serviceLineId?: string;
  daysOverdue?: string | number;
};

export type InvoiceCellContext = {
  row: IBaseInvoice;
  t: Translate;
  onEditRow: () => void;
  invoiceUrl: string;
  /**
   * Currency for the amount cells. Callers resolve this per grid: the invoice's
   * own currency where the endpoint returns it, otherwise a page-level fallback
   * such as the customer's service-line currency.
   */
  currency: string;
};

const renderCurrencyCell = (value: unknown, currency: string, emptyFallback = '-') => {
  if (value === null || value === undefined || value === '') {
    return emptyFallback;
  }

  return fCurrency(Number(value), { minimumFractionDigits: 2, currency });
};

const renderDateCell = (
  value: unknown,
  highlightPast = false,
  emptyFallback = '-',
  dimEmpty = false
) => (
  <ListItemText
    primary={value ? fDate(value as string) : emptyFallback}
    slotProps={{
      primary:
        !value && dimEmpty
          ? { variant: 'body2', color: 'textDisabled', noWrap: true }
          : {
              typography: 'body2',
              noWrap: true,
              sx: {
                color:
                  highlightPast && value && fIsBeforeToday(value as string) ? 'error.main' : '',
              },
            },
    }}
  />
);

const renderInvoiceLink = ({ row, onEditRow, invoiceUrl }: InvoiceCellContext) => (
  <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
    <ListItemText
      disableTypography
      primary={
        <Link
          data-test-id="invoice-no-link"
          onClick={onEditRow}
          variant="body2"
          href={invoiceUrl}
          sx={{
            cursor: 'pointer',
            color: 'warning.contrastText',
            textDecoration: 'underline',
          }}
        >
          <ShortenCode value={row.invoiceNo} maxLength={15} typography="body2" />
        </Link>
      }
    />
  </Box>
);

const renderStage = ({ row, t }: InvoiceCellContext) => (
  <Label
    variant="soft"
    color={
      (row.stage === InvoiceStage.Invoiced && 'info') ||
      (row.stage === InvoiceStage.Reminded && 'warning') ||
      (row.stage === InvoiceStage.DebtCollection && 'error') ||
      'default'
    }
    sx={{ width: 'fit-content' }}
  >
    {t(`pages.content.invoiceStages.${row.stage}`)}
  </Label>
);

const renderStatus = ({ row, t }: InvoiceCellContext) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Label
      variant="filled"
      color={
        (row.status === InvoiceStatus.Open && 'success') ||
        (row.status === InvoiceStatus.Closed && 'default') ||
        (row.status === InvoiceStatus.Cancelled && 'error') ||
        (row.status === InvoiceStatus.Paused && 'warning') ||
        'default'
      }
      sx={{ width: 'fit-content' }}
    >
      {t(`pages.content.invoiceStatuses.${row.status}`)}
    </Label>

    {row.paymentStatus === InvoicePaymentStatus.Paid &&
      row.lastPaymentTransactionType &&
      LAST_PAYMENT_TYPES_TO_SHOW.includes(row.lastPaymentTransactionType) && (
        <Label variant="inverted" color="default" sx={{ width: 'fit-content' }}>
          {t(`filters.labels.${row.lastPaymentTransactionType}`)}
        </Label>
      )}
  </Box>
);

const renderDistributionChannel = (invoice: InvoiceCellRow, t: Translate) => {
  // Keeps the disabled label flush with where the channel icon starts, rather
  // than centered by the cell's `align`.
  const wrapperSx = { gap: 1, display: 'flex', alignItems: 'center' };

  if (!invoice.distributionChannel) {
    return (
      <Box sx={wrapperSx}>
        <Typography variant="body2">
          {t('pages.content.distributionChannels.distributionDisabled')}
        </Typography>
      </Box>
    );
  }

  let iconName;

  switch (invoice.distributionChannel) {
    case 'edi':
      iconName = 'lucide:file-text';
      break;

    case 'e-invoice':
      iconName = 'lucide:laptop';
      break;

    case 'paper':
      iconName = 'lucide:scroll';
      break;

    case 'sms':
      iconName = 'lucide:smartphone';
      break;

    // Defaults to EMAIL
    default:
      iconName = 'lucide:mail';
      break;
  }

  return (
    <Box sx={wrapperSx}>
      <Iconify icon={iconName} width={16} sx={{ color: 'text.secondary' }} />
      <Typography variant="body2">
        {t(`pages.content.distributionChannels.${invoice.distributionChannel}`, {
          defaultValue: invoice.distributionChannel,
        })}
      </Typography>
    </Box>
  );
};

const PAUSE_LEVEL_ICONS: Record<string, string> = {
  invoicePause: 'lucide:file-text',
  customerPause: 'lucide:users-round',
  scheduledPause: 'lucide:layers',
};

const renderPauseCell = (
  value: string | null | undefined,
  iconName: string,
  labelKeyPrefix: string,
  t: Translate
) => {
  if (!value) return '-';

  return (
    <Box sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
      <Iconify icon={iconName} width={16} sx={{ color: 'text.secondary' }} />
      <Typography variant="body2" noWrap>
        {t(`${labelKeyPrefix}.${value}`, { defaultValue: value })}
      </Typography>
    </Box>
  );
};

const renderPaymentStatus = ({ row, t }: InvoiceCellContext) => (
  <Label
    variant="soft"
    color={
      (row.paymentStatus === InvoicePaymentStatus.Paid && 'success') ||
      (row.paymentStatus === InvoicePaymentStatus.NotPaid && 'error') ||
      (row.paymentStatus === InvoicePaymentStatus.PartiallyPaid && 'warning') ||
      (row.paymentStatus === InvoicePaymentStatus.CapitalPaid && 'secondary') ||
      'default'
    }
    sx={{ width: 'fit-content' }}
  >
    {t(`pages.content.invoicePaymentStatuses.${row.paymentStatus}`)}
  </Label>
);

/**
 * Renders the cell body for one invoice column.
 *
 * Shared by every customizable invoice grid so that a column added to the view
 * definitions is rendered identically wherever it is switched on. Columns with
 * no case here fall through to `-`.
 */
export const renderInvoiceCell = (columnId: string, context: InvoiceCellContext): ReactNode => {
  const { row, t, onEditRow, currency } = context;

  const invoice = row as unknown as InvoiceCellRow;

  switch (columnId) {
    case InvoiceColumns.CUSTOMER_ID:
      return (
        <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
          <UserAvatar
            dataTestId="customer-link"
            name={row.customer.customerName}
            subText={row.customer.customerNo}
            to={row.customer.id}
          />
        </Box>
      );

    case InvoiceColumns.CUSTOMER_NO:
      return (
        <ListItemText
          disableTypography
          primary={
            <Typography variant="body2" noWrap>
              {row.customer.customerName || '-'}
            </Typography>
          }
          secondary={
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.customer.customerNo || '-'}
            </Typography>
          }
        />
      );

    case InvoiceColumns.INVOICE_NUMBER:
      return renderInvoiceLink(context);

    case InvoiceColumns.CLIENT_ID:
    case InvoiceColumns.READABLE_CLIENT_ID:
      return (
        <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
          <ClientAvatar logo={row.client?.logo_uri} name={row.client?.name} />
        </Box>
      );

    case InvoiceColumns.COUNTRY:
      return <CountryWithFlag countryCode={row.countryCode} />;

    case InvoiceColumns.DUE_DATE:
      return renderDateCell(row.dueDate, true);

    case InvoiceColumns.INVOICE_DATE:
      return renderDateCell(invoice.invoiceDate);

    case InvoiceColumns.TOTAL_REMAINING_BALANCE:
      return renderCurrencyCell(invoice.remainingAmount ?? row.totalRemainingBalance, currency);

    case InvoiceColumns.ORIGINAL_TOTAL_AMOUNT:
      return renderCurrencyCell(invoice.originalTotalAmount, currency);

    case InvoiceColumns.PAID_AMOUNT:
      return renderCurrencyCell(invoice.paidAmount, currency);

    case InvoiceColumns.STAGE:
      return renderStage(context);

    case InvoiceColumns.STATUS:
      return renderStatus(context);

    case InvoiceColumns.PAYMENT_STATUS:
      return renderPaymentStatus(context);

    case InvoiceColumns.SERVICE_LINE_ID:
      return invoice.serviceLineId || '-';

    case InvoiceColumns.CLIENT_NAME:
      return invoice.client.name || '-';

    case InvoiceColumns.VAT_AMOUNT:
      return renderCurrencyCell(invoice.vatAmount, currency);

    case InvoiceColumns.PAYMENT_REFERENCE:
      return invoice.paymentReference || '-';

    case InvoiceColumns.CURRENCY:
      return row.currency || '-';

    case InvoiceColumns.PDF_LINK:
      return invoice.pdfURL ? (
        <Link
          href={invoice.pdfURL}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'secondary.main',
          }}
        >
          <Iconify icon="lucide:eye" width={18} />
          <Typography variant="body2">{t('tables.columns.viewPdf')}</Typography>
        </Link>
      ) : (
        '-'
      );
    // New fields
    case InvoiceColumns.SERVICE_LINE_NAME:
      return invoice.serviceLineName;

    case InvoiceColumns.FEE:
      return !invoice.fee || Number(invoice.fee) === 0 ? (
        <Typography variant="body2" color="textDisabled">
          {t('pages.content.noFeeApplied')}
        </Typography>
      ) : (
        renderCurrencyCell(invoice.fee, currency)
      );

    case InvoiceColumns.PAYMENT_METHOD:
      return invoice.paymentMethod ? (
        getTransactionTypeLabel(invoice.paymentMethod, t)
      ) : (
        <Typography variant="body2" color="error.main">
          {t('pages.content.notPaidYet')}
        </Typography>
      );

    case InvoiceColumns.PAYMENT_DATE:
      return renderDateCell(invoice.paymentDate, false, t('pages.content.missingDate'), true);

    case InvoiceColumns.lAST_ESCALATION_DATE:
      return renderDateCell(invoice.lastEscalationDate);

    case InvoiceColumns.WHEN_ESCALATING_TO_DEBT_COLLECTION:
      return renderDateCell(invoice.whenEscalatingToDebtCollection);

    case InvoiceColumns.DAYS_OVERDUE:
      return invoice.daysOverdue;

    case InvoiceColumns.ORG_NO:
      return invoice.orgNo;

    case InvoiceColumns.DISTRIBUTION_CHANNEL:
      return renderDistributionChannel(invoice, t);

    case InvoiceColumns.REMINDER_DATE:
      return renderDateCell(invoice.reminderDate);

    case InvoiceColumns.PAUSED_TYPE:
      return renderPauseCell(
        invoice.pausedType,
        'lucide:triangle-alert',
        'pages.content.pauseTypes',
        t
      );

    case InvoiceColumns.PAUSED_LEVEL:
      return renderPauseCell(
        invoice.pausedLevel,
        (invoice.pausedLevel && PAUSE_LEVEL_ICONS[invoice.pausedLevel]) || 'lucide:users-round',
        'pages.content.pauseLevels',
        t
      );

    case InvoiceColumns.PAUSED_DURATION:
      return renderDateCell(
        invoice.pausedDuration,
        false,
        invoice.pausedType ? t('pages.content.serviceLineStatuses.indefinite') : '-'
      );

    case INVOICE_ACTION_COLUMN_ID:
      return (
        <IconButton color="default" onClick={onEditRow} data-test-id="edit-row-link">
          <Iconify icon="lucide:eye" />
        </IconButton>
      );

    default:
      return '-';
  }
};

/**
 * Pins the action column to the right edge during horizontal scroll.
 * `useCustomizableTable` makes the matching header cell sticky and leaves the
 * body cells to the row component.
 *
 * A sticky cell needs its own opaque background or the cells passing underneath
 * show through. The row's hover and selected tints are translucent overlays, so
 * they are composited over that base rather than replacing it, which would
 * otherwise leave the pinned cell unhighlighted while its row lights up.
 */
const pinnedActionCellSx: SxProps<Theme> = (theme) => ({
  px: 1,
  position: 'sticky',
  right: 0,
  // Above the body cells scrolling past, below the sticky header cell (25).
  zIndex: 2,
  backgroundColor: theme.palette.background.paper,
  '.MuiTableRow-root:hover > &': {
    backgroundImage: `linear-gradient(${theme.palette.action.hover}, ${theme.palette.action.hover})`,
  },
  '.MuiTableRow-root.Mui-selected > &': {
    backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
  },
});

export const getInvoiceCellSx = (columnId: string): SxProps<Theme> | undefined => {
  if (AMOUNT_COLUMN_IDS.includes(columnId as InvoiceColumns)) {
    return { fontWeight: 600 };
  }

  if (columnId === INVOICE_ACTION_COLUMN_ID) {
    return pinnedActionCellSx;
  }

  return undefined;
};
